import { useState } from 'react';
import { useQuery as useConvexQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Download } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Select from '../components/Select';
import Table from '../components/Table';
import Badge from '../components/Badge';
import { formatDateTime, downloadCSV } from '../utils/helpers';

export default function ActivityLogs() {
    const [actionFilter, setActionFilter] = useState('all');

    const logs = useConvexQuery(api.activityLogs.getRecentLogs, { limit: 100 });

    const filteredLogs = logs
        ? logs.filter((log) => actionFilter === 'all' || log.action === actionFilter)
        : [];

    const uniqueActions = logs
        ? [...new Set(logs.map((log) => log.action))].sort()
        : [];

    const handleExport = () => {
        if (!filteredLogs.length) return;

        const exportData = filteredLogs.map((log) => ({
            Timestamp: formatDateTime(log.timestamp),
            Admin: log.adminId,
            Action: log.action,
            TargetType: log.targetType || '',
            TargetID: log.targetId || '',
            Details: JSON.stringify(log.details || {}),
        }));

        downloadCSV(exportData, `activity-logs-${Date.now()}.csv`);
    };

    const columns = [
        {
            header: 'Timestamp',
            accessor: 'timestamp',
            render: (log) => (
                <span className="text-sm">{formatDateTime(log.timestamp)}</span>
            ),
        },
        {
            header: 'Action',
            accessor: 'action',
            render: (log) => (
                <Badge variant="primary">{log.action}</Badge>
            ),
        },
        {
            header: 'Target',
            accessor: 'targetType',
            render: (log) => (
                <span className="text-sm">
                    {log.targetType ? `${log.targetType}:${log.targetId?.substring(0, 8)}` : 'N/A'}
                </span>
            ),
        },
        {
            header: 'Admin',
            accessor: 'adminId',
            render: (log) => (
                <span className="text-sm font-mono">
                    {log.adminId.substring(0, 12)}...
                </span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Activity Logs</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        View all admin actions and system events
                    </p>
                </div>
                <Button
                    variant="secondary"
                    onClick={handleExport}
                    disabled={filteredLogs.length === 0}
                >
                    <Download size={20} />
                    Export CSV
                </Button>
            </div>

            <Card>
                <div className="mb-6">
                    <Select
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                        className="w-64"
                    >
                        <option value="all">All Actions</option>
                        {uniqueActions.map((action) => (
                            <option key={action} value={action}>
                                {action}
                            </option>
                        ))}
                    </Select>
                </div>

                <Table
                    columns={columns}
                    data={filteredLogs}
                    loading={logs === undefined}
                    emptyState={{
                        title: 'No activity logs',
                        description: 'Activity will appear here as admins take actions',
                    }}
                />
            </Card>
        </div>
    );
}

